/**
 * ResponsiveMaterialTable
 *
 * Wrapper autour de MaterialTable qui reproduit le comportement de
 * Bootstrap DataTables Responsive :
 *  - Mesure la largeur RÉELLE du conteneur (ResizeObserver) — pas la fenêtre.
 *  - Calcule combien de colonnes tiennent dans l'espace disponible.
 *  - Un bouton "▶" révèle les colonnes cachées sous forme de paires label/valeur.
 */

import React, { useState, useEffect, useRef } from 'react';
import MaterialTable from 'material-table';

// Largeur minimale estimée par colonne (en px)
// On prend 150px pour que les colonnes avec des titres longs restent lisibles
const MIN_COL_WIDTH = 150;

function getVisibleCount(containerWidth, totalCols) {
  if (!containerWidth) return totalCols;
  // On réserve ~50px pour le bouton d'expansion
  const available = containerWidth - 50;
  const count = Math.max(1, Math.floor(available / MIN_COL_WIDTH));
  return Math.min(count, totalCols);
}

export default function ResponsiveMaterialTable({ columns, detailPanel, ...rest }) {
  const wrapperRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(null);

  useEffect(() => {
    if (!wrapperRef.current) return;

    const el = wrapperRef.current;
    const update = (entries) => {
      const width = entries ? entries[0].contentRect.width : el.offsetWidth;
      setContainerWidth(width);
    };

    // Lecture initiale
    setContainerWidth(el.offsetWidth);

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    } else {
      // Fallback : écoute resize de la fenêtre
      const onResize = () => setContainerWidth(el.offsetWidth);
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }
  }, []);

  const totalCols = columns ? columns.length : 0;
  const visibleCount = getVisibleCount(containerWidth, totalCols);
  const needsExpansion = containerWidth !== null && visibleCount < totalCols;

  const visibleColumns = needsExpansion ? columns.slice(0, visibleCount) : columns;
  const hiddenColumns  = needsExpansion ? columns.slice(visibleCount)    : [];

  // Panneau d'expansion : affiche les colonnes cachées  
  const responsiveDetailPanel = (rowData) => (
    <div style={{
      padding: '10px 16px 14px 48px',
      background: '#f9f9f9',
      borderTop: '1px solid #e8e8e8',
    }}>
      {hiddenColumns.map((col) => {
        const rawValue = col.field ? rowData[col.field] : undefined;
        const rendered = col.render ? col.render(rowData) : rawValue;
        if (rendered === null || rendered === undefined || rendered === '') return null;
        return (
          <div
            key={col.field || col.title}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              padding: '5px 0',
              borderBottom: '1px solid #ececec',
              fontSize: '0.85rem',
              lineHeight: 1.5,
            }}
          >
            <span style={{
              fontWeight: 600,
              minWidth: '180px',
              maxWidth: '180px',
              color: '#555',
              marginRight: '12px',
              flexShrink: 0,
            }}>
              {col.title}
            </span>
            <span style={{ color: '#222', wordBreak: 'break-word' }}>
              {rendered}
            </span>
          </div>
        );
      })}
    </div>
  );

  let mergedDetailPanel;
  if (!needsExpansion) {
    mergedDetailPanel = detailPanel;
  } else if (detailPanel) {
    const existingPanels = Array.isArray(detailPanel) ? detailPanel : [detailPanel];
    mergedDetailPanel = [
      { tooltip: 'Voir plus', render: responsiveDetailPanel },
      ...existingPanels,
    ];
  } else {
    mergedDetailPanel = responsiveDetailPanel;
  }

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <MaterialTable
        columns={visibleColumns}
        detailPanel={mergedDetailPanel}
        {...rest}
      />
    </div>
  );
}
